import React, { useState, useEffect, useRef } from "react";
import TeamNotes from "../../database/TeamNotes.js";
import { CAPTURE_EXCLUDE_ATTR } from "../../lib/screenshot.js";
import { leaveComment } from "../../lib/pinScreenshot.js";
import { uploadFile } from "../../lib/upload.js";

// Turn a JPEG dataURL into a File for the upload library.
function dataUrlToFile(dataUrl, name) {
    const [meta, b64] = dataUrl.split(",");
    const mime = /data:(.*?);base64/.exec(meta)?.[1] || "image/jpeg";
    const bytes = atob(b64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    return new File([arr], name, { type: mime });
}

// Platform-injected floating feedback widget (Team Notes), mounted by main.jsx
// into its own React root when window.__CONFIG__.teamNotesEnabled. Figma-style:
// the FAB starts comment mode (cursor → comment pin), the user clicks a spot,
// an inline one-line bar opens in place, and sending uploads a pinned
// screenshot + the note. Inline styles only (purged Tailwind can't touch it).
const Z = 2147482000;

const fabStyle = {
    position: "fixed",
    bottom: "18px",
    right: "18px",
    zIndex: Z,
    width: "44px",
    height: "44px",
    borderRadius: "50%",
    border: "none",
    background: "#111827",
    color: "#fff",
    fontSize: "20px",
    cursor: "pointer",
    boxShadow: "0 4px 14px rgba(0,0,0,.25)",
};

const toastStyle = (kind) => ({
    position: "fixed",
    bottom: "72px",
    right: "18px",
    zIndex: Z,
    maxWidth: "300px",
    background: kind === "ok" ? "#16a34a" : "#b91c1c",
    color: "#fff",
    font: "500 13px system-ui",
    padding: "10px 14px",
    borderRadius: "10px",
    boxShadow: "0 8px 24px rgba(0,0,0,.25)",
});

export default function TeamNotesWidget() {
    const [busy, setBusy] = useState(false);
    const [active, setActive] = useState(false); // in comment-placing mode
    const [toast, setToast] = useState(null); // {kind, text}
    // Re-evaluate visibility when the workflow-auth token changes in ANOTHER
    // tab (login/logout) — isEnabled() reads localStorage, which doesn't
    // re-render on its own.
    const [, forceReeval] = useState(0);
    const toastTimerRef = useRef(null);

    useEffect(() => {
        const onStorage = (e) => {
            if (!e.key || e.key.startsWith("workflow-auth-")) {
                forceReeval((n) => n + 1);
            }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    if (!TeamNotes.isEnabled()) return null;

    const flash = (kind, text, ms = 2600) => {
        setToast({ kind, text });
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        toastTimerRef.current = setTimeout(() => setToast(null), ms);
    };

    const start = async () => {
        if (busy || active) return; // don't stack overlays
        // leaveComment shows the comment cursor, waits for a click, then opens
        // the inline compose bar and resolves { text, dataUrl } (pinned shot).
        // Esc / outside click resolves null (cancel).
        setActive(true);
        let result;
        try {
            result = await leaveComment();
        } finally {
            setActive(false);
        }
        if (!result) return; // cancelled
        setBusy(true);
        try {
            let screenshotFileId = null;
            if (result.dataUrl) {
                const file = dataUrlToFile(
                    result.dataUrl,
                    `team-note-${Date.now()}.jpg`
                );
                const uploaded = await uploadFile(file, { isPublic: false });
                screenshotFileId = uploaded?.fileId || null;
            }
            await TeamNotes.create({ text: result.text, screenshotFileId });
            flash("ok", "Sent! The app owner will see your note.");
        } catch (error) {
            const text =
                typeof error?.message === "string"
                    ? error.message
                    : "Couldn't send your note. Please try again.";
            flash("err", text);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div {...{ [CAPTURE_EXCLUDE_ATTR]: "" }}>
            {toast && <div style={toastStyle(toast.kind)}>{toast.text}</div>}
            <button
                style={{
                    ...fabStyle,
                    background: active ? "#2563eb" : fabStyle.background,
                }}
                title="Leave a comment for the app owner"
                aria-label="Leave a comment for the app owner"
                onClick={start}
                disabled={busy}
            >
                {busy ? "…" : "💬"}
            </button>
        </div>
    );
}
